import {customersApiClient} from '@/services/apiClient';

/**
 * REPORTED DELAYS — "I'm stuck at immigration, give me 15 minutes."
 *
 * ── ⚠️ THERE IS EXACTLY ONE ENDPOINT, AND IT IS A WRITE ───────────────────────────
 * There is no delay list, no delay status and no way to read a single delay back. Delays
 * arrive INSIDE the trip detail, as `requested_delays` and `active_requested_delay`. So
 * anything that needs to know about a delay reads the trip; only reporting one comes here.
 *
 * ── ⚠️ AND THE ENVELOPE NESTS INCONSISTENTLY ──────────────────────────────────────
 * The mobile client probes several paths for the created record rather than assuming one,
 * and so does this. `apiClient` has already unwrapped `data`, so the paths below are what
 * is left after that.
 */

/** The wire path. Written once so a rename is one edit. */
const delaysPath = (tripId) => `/trips/${tripId}/requested-delays`;

/** First path that holds something. Same helper as `chatService`, same reason. */
function pick(source, paths) {
  for (const path of paths) {
    const value = path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), source);
    if (value !== undefined && value !== null) return value;
  }
  return null;
}

/**
 * One delay record, coerced.
 *
 * ⚠️ A ROW WITH NO NUMERIC `id` IS NOT A DELAY. Everything downstream keys on it — the
 * React keys of the five bubbles, the banner, the dedupe against the polled trip — so a
 * record without one is dropped rather than rendered with an undefined key.
 *
 * Fields are kept under their WIRE names. They travel straight back into the trip payload's
 * `requested_delays`, and renaming them here would mean two shapes for one thing depending
 * on whether it arrived from the POST or from the poll.
 */
export function parseRequestedDelay(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = Number(raw.id);
  if (!Number.isFinite(id)) return null;

  return {
    id,
    trip_id: raw.trip_id ?? null,
    reason_code: String(raw.reason_code ?? ''),
    reason_text: String(raw.reason_text ?? ''),
    minutes: Number(raw.minutes) || 0,
    requested_at: raw.requested_at ?? null,
    expires_at: raw.expires_at ?? null,
    scheduled_at_after: raw.scheduled_at_after ?? null,
  };
}

export const tripDelayService = {
  /**
   * Tell the driver why, and for how long.
   *
   * ⚠️ `reason_text` IS NOT ALWAYS THE CATALOG LABEL. For `other_delay` it is the
   * passenger's own words — that is the whole point of that row — and the driver's app
   * renders this string verbatim. See `tripDelayReasons`.
   *
   * ⚠️ THE ERROR `key` IS RETURNED, NOT JUST THE MESSAGE. `requested_delay_active` has to
   * be told apart from every other failure because it is the one that is not the
   * passenger's fault and not worth retrying — a delay is already running.
   *
   * @param {string|number} tripId
   * @param {{reasonCode: string, reasonText: string, minutes: number}} report
   */
  async report(tripId, {reasonCode, reasonText, minutes}) {
    const result = await customersApiClient.post(delaysPath(tripId), {
      reason_text: reasonText,
      reason_code: reasonCode,
      minutes,
    });

    if (!result.success) {
      return {
        success: false,
        delay: null,
        scheduledAt: null,
        key: result.raw?.key || null,
        error: result.error || 'Could not report this delay.',
      };
    }

    const delay = parseRequestedDelay(
      pick(result.data, ['requested_delay', 'data.requested_delay']) || result.data,
    );

    /* ⚠️ A 2xx WITH NOTHING PARSEABLE IS A FAILURE, not a success with a null delay. The
       caller writes this record into the trip cache; letting an empty one through would
       blank the banner and re-offer the entry point the passenger just used. */
    if (!delay) {
      return {success: false, delay: null, scheduledAt: null, key: null, error: 'Could not report this delay.'};
    }

    return {
      success: true,
      delay,
      /* The pickup time moves when a delay is accepted. The server says how far; the
         record's own `scheduled_at_after` is the fallback when it does not. */
      scheduledAt: pick(result.data, ['scheduled_at', 'data.scheduled_at']) || delay.scheduled_at_after,
      key: result.key || null,
      error: null,
    };
  },
};

export default tripDelayService;
